import { TableCell, TDocumentDefinitions } from "pdfmake/interfaces";

import { generateFontSize, getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import { HeaderLogoWithAddress } from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

type WorkHoursEmployee = {
  abbrName: string;
  absent_slot_count?: number;
  carry_over_deficit?: number;
  carry_over_ot?: number;
  cutoff_over_ot?: string;
  early_slot_count?: number;
  employee_code: string;
  late_slot_count?: number;
  manual_summary?: Record<string, number>;
  ot_1_5hr?: number | string;
  ot_1hr?: number | string;
  ot_3hr?: number | string;
  remaining_actual_hour?: number;
  remaining_over_deficit: number;
  sum_ot: number;
  template_summary?: Record<string, number>;
  total_early_minutes?: number;
  total_late_minutes?: number;
};

type FormWorkHoursSummaryReportProps = {
  fields: WorkHoursEmployee[];
  params: {
    month: string;
    ot_cutoff_date: string;
    printed_by: string;
    printed_date: string;
  };
};

const FormWorkHoursSummaryReport = async (
  props: FormWorkHoursSummaryReportProps
): Promise<TDocumentDefinitions> => {
  const font = getCompanyFont();
  const fontSizes = generateFontSize(font);

  const headerForm = await HeaderLogoWithAddress({
    font: getCompanyFont(),
    pageMargins: [0, 95, 0, 40],
    titleContent: [
      {
        alignment: "center",
        margin: [0, -5, 0, 0],
        style: { bold: true, fontSize: fontSizes[18] },
        text: "Work Hours Summary Report",
      },
    ],
  });

  const { images, lineHeights, styles } = headerForm;

  const baseColumnWidths = [
    "11%",
    "5%",
    "5%",
    "6%",
    "5%",
    "5%",
    "5%",
    "5%",
    "5%",
    "5%",
    "5%",
    "8%",
    "6%",
    "8%",
    "5%",
    "5.5%",
    "5.5%",
  ];

  // Generate main table rows
  const mainTableRows: TableCell[][] = [];

  for (const item of props.fields) {
    // Employee main data row
    mainTableRows.push([
      { stack: [{ text: item.employee_code }, { text: item.abbrName }] },
      { alignment: "center", text: String(item.carry_over_ot) },
      { alignment: "center", text: String(item.carry_over_deficit) },
      { alignment: "center", text: item.cutoff_over_ot },
      { alignment: "center", text: String(item.sum_ot) },
      { alignment: "center", text: item.ot_1hr },
      { alignment: "center", text: item.ot_1_5hr },
      { alignment: "center", text: item.ot_3hr },
      { alignment: "center", text: item.remaining_actual_hour },
      { alignment: "center", text: String(item.remaining_over_deficit) },
      { alignment: "center", text: String(item.late_slot_count) },
      { alignment: "center", text: String(item.total_late_minutes) },
      { alignment: "center", text: String(item.early_slot_count) },
      { alignment: "center", text: String(item.total_early_minutes) },
      { alignment: "center", text: String(item.absent_slot_count) },
      { text: "" }, // Sign Off Employee column
      { text: "" }, // Sign Off Manager column
    ]);

    const allTotalSlotKeys = Object.keys({ ...item.template_summary, ...item.manual_summary });
    const allTotalSlotValues = Object.values({ ...item.template_summary, ...item.manual_summary });
    const totalSlot = allTotalSlotValues.reduce((result, value) => result + value, 0);

    // Total Slot row 1 - key names
    const totalSlotKeyCells: TableCell[] = [
      {
        alignment: "center",
        rowSpan: 2,
        stack: [{ text: "Total Slot/ Month :" }, { text: totalSlot }],
        style: "tableHeader",
      },
    ];

    // Add key names in separate cells
    for (const key of allTotalSlotKeys) {
      totalSlotKeyCells.push({
        alignment: "center",
        style: { bold: true, fontSize: fontSizes[11] },
        text: key,
      });
    }

    // Fill remaining cells
    const remainingCells = baseColumnWidths.length - totalSlotKeyCells.length;

    for (const [index] of Array.from({ length: remainingCells }).entries()) {
      totalSlotKeyCells.push({
        border: [false, false, remainingCells - 1 === index, true],
        text: "",
      });
    }

    mainTableRows.push(totalSlotKeyCells);

    // Total Slot row 2 - values
    const totalSlotValueCells: TableCell[] = [{ text: "" }];

    // Add values in separate cells
    for (const value of allTotalSlotValues) {
      totalSlotValueCells.push({
        alignment: "center",
        style: { fontSize: fontSizes[11] },
        text: value,
      });
    }

    // Fill remaining cells
    const remainingValueCells = baseColumnWidths.length - totalSlotValueCells.length;

    for (const [index] of Array.from({ length: remainingValueCells }).entries()) {
      totalSlotValueCells.push({
        border: [false, false, remainingValueCells - 1 === index, true],
        text: "",
      });
    }

    mainTableRows.push(totalSlotValueCells);
  }

  return {
    defaultStyle: {
      font,
      fontSize: fontSizes[12],
      lineHeight: lineHeights[1],
    },
    pageOrientation: "landscape",
    pageSize: "A4",
    ...headerForm,
    images: { ...images },
    styles: {
      ...styles,
      footerData: {
        fontSize: fontSizes[10],
      },
      parameterBox: {
        fillColor: "#f0f0f0",
        fontSize: fontSizes[15],
        margin: [5, 5, 5, 5],
      },
      tableHeader: {
        bold: true,
        fillColor: "#f0f0f0",
        fontSize: fontSizes[11],
        lineHeight: lineHeights[0.85],
      },
    },
    content: [
      // Parameter section
      {
        columns: [
          {
            text: [
              { style: "parameterBox", text: "Month: " },
              { bold: true, style: "parameterBox", text: props.params.month },
            ],
            width: "auto",
          },
          {
            margin: [20, 0, 0, 0],
            text: [
              { style: "parameterBox", text: "Apply OT cut off date: " },
              {
                bold: true,
                style: "parameterBox",
                text: props.params.ot_cutoff_date,
              },
            ],
            width: "auto",
          },
          {
            text: "",
            width: "*",
          },
        ],
        margin: [15, 0, 15, 5],
      },

      // Main work hours table
      {
        margin: [15, 0, 15, 10],
        style: "tableStyle",
        table: {
          headerRows: 2,
          widths: baseColumnWidths,
          body: [
            // First header row - main groupings
            [
              {
                alignment: "center",
                marginTop: 13,
                rowSpan: 2,
                style: "tableHeader",
                text: "Employee",
              },
              {
                alignment: "center",
                colSpan: 2,
                style: "tableHeader",
                text: "Carry Over",
              },
              {},
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "OT cut off\nTotal (hr.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "Sum OT\n(hr.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "OT x 1\n(hr.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "OT x 1.5\n(hr.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "OT x 3\n(hr.)",
              },
              {
                alignment: "center",
                colSpan: 2,
                style: "tableHeader",
                text: "Remaining",
              },
              {},
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "No.of\nlate",
              },
              {
                alignment: "center",
                marginTop: 7,
                rowSpan: 2,
                style: "tableHeader",
                text: "Accumulated\nLate time (min.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "No.of\nEarly out",
              },
              {
                alignment: "center",
                marginTop: 1,
                rowSpan: 2,
                style: "tableHeader",
                text: "Accumulated\nEarly out\ntime (min.)",
              },
              {
                alignment: "center",
                marginTop: 8,
                rowSpan: 2,
                style: "tableHeader",
                text: "No.of\nAbsent",
              },
              {
                alignment: "center",
                colSpan: 2,
                style: "tableHeader",
                text: "Sign Off",
              },
              {},
            ],
            // Second header row - specific columns
            [
              {},
              {
                alignment: "center",
                marginTop: 5,
                style: "tableHeader",
                text: "OT (hr.)",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Deficit\n(hr.)",
              },
              {},
              {},
              {},
              {},
              {},
              {
                alignment: "center",
                style: "tableHeader",
                text: "OT\n(hr.)",
              },
              {
                alignment: "center",
                style: "tableHeader",
                text: "Deficit\n(hr.)",
              },
              {},
              {},
              {},
              {},
              {},
              {
                alignment: "center",
                marginTop: 5,
                style: "tableHeader",
                text: "Employee",
              },
              {
                alignment: "center",
                marginTop: 5,
                style: "tableHeader",
                text: "Manager",
              },
            ],
            ...mainTableRows,
          ],
        },
        layout: {
          hLineColor: () => "#cccccc",
          hLineWidth: (index, node) => {
            if (index === 0 || index === 2 || index === node.table.body.length) {
              return 0.5;
            }

            return 0.2;
          },
          paddingBottom: () => 2,
          paddingTop: () => 2,
          vLineColor: () => "#cccccc",
          vLineWidth: () => 0.3,
        },
      },
    ],
    footer(currentPage: number, pageCount: number) {
      return {
        margin: [0, 0, 0, 10],
        stack: [
          {
            columns: [
              {
                alignment: "left",
                italics: true,
                margin: [15, 5, 0, 0],
                style: "footerData",
                text: [
                  { bold: true, text: "Carry Over" },
                  " - Remaining OT/Deficit hours from the previous month; ",
                  { bold: true, text: "Remaining" },
                  " - OT/Deficit hours remaining after the cut-off date for the current month.",
                ],
              },
            ],
          },
          {
            columns: [
              {
                alignment: "left",
                italics: true,
                margin: [15, 2.5, 0, 0],
                style: "footerData",
                text: "**Late, Early Out, and Absent refer to the accumulated number of occurrences or hours in the current month.",
                width: "*",
              },
              {
                alignment: "right",
                italics: true,
                margin: [0, 2.5, 40, 0],
                noWrap: true,
                style: "footerData",
                text: `Printed date : ${props.params.printed_date} ; by ${props.params.printed_by}`,
                width: "auto",
              },
              {
                alignment: "right",
                margin: [0, 2.5, 15, 0],
                noWrap: true,
                style: "footerData",
                text: `Page ${currentPage} from ${pageCount}`,
                width: "auto",
              },
            ],
          },
        ],
      };
    },
  };
};

export default FormWorkHoursSummaryReport;
